/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { BehaviorSubject, Observable, from, map, switchMap } from 'rxjs';

import { DemoService } from './demo.service';
import { WsClientService } from './ws-client.service';
import { environment } from '../../environments/environment';
import { AppPhase, DealerConsoleAction, FlagStateType, GameType, TableMode } from '../models/communication/app-enums.model';
import {
    AppEventType,
    CardBurnEvent,
    CardDealtEvent,
    ChatMessageEvent,
    FlagStateChangeEvent,
    GamePhaseChangeEvent,
    GameStateChangeEvent,
    LoginChangeEvent,
    NewRoundPreparationPhaseChangeEvent,
    PlayerCountChangeEvent,
    RoundEndPhaseChangeEvent,
    RoundErrorPhaseChangeEvent
} from '../models/communication/app-events.model';
import {
    ChangeGameRequestMessage,
    DealerActionRequestMessage,
    DealerRequestType,
    GetChatMessagesResponseMessage,
    GetGamesResponseMessage,
    SetFlagStateRequestMessage
} from '../models/communication/app-requests.model';
import { AppState } from '../models/communication/app-state.model';
import { YEventPayload } from '../models/y-protocol.model';

@Injectable({
    providedIn: 'root'
})
export class DealerService extends WsClientService<AppState> {
    private static readonly maxChatMessageDisplayCount = 50;

    // Set to true to enable demo mode
    private demoMode = false;
    private readonly stateSubject = new BehaviorSubject<AppState | undefined>(undefined);
    private state!: AppState;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly demoService: DemoService
    ) {
        super();
        this.getInitData()
            .then(async (state) => {
                this.state = state;
                return Promise.all([this.sendGetChatMessagesRequest(), this.sendGetGamesRequest()]);
            })
            .then(([chatMessages, games]) => {
                this.state = { ...this.state, chatMessages, games };
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (this.state.configuration.tableMode === undefined) {
                    this.state.configuration.tableMode = TableMode.Online;
                }
                this.stateSubject.next(this.state);

                this.getEventsObservable().subscribe((yEventPayload) => {
                    this.handleYEvent(yEventPayload);
                });
            })
            .catch((error) => {
                console.error('Failed to get initial state', error);
            });
    }

    override async getInitData(): Promise<AppState> {
        const url = await this.determineDemoModeAndGetWsUrl();

        if (url) {
            return super.getInitData(this.appendTimestampToUrl(url));
        } else {
            this.demoService.start();
            return this.demoService.getInitData();
        }
    }

    override getEventsObservable(): Observable<YEventPayload<AppEventType>> {
        return from(this.getQueryParams()).pipe(
            map((paramMap: ParamMap) => paramMap.get('ws')),
            switchMap((wsParam) => {
                // Note: this complication is needed because getEventsObservable() method is called before this.demoMOde is set in
                // determineDemoModeAndGetWsUrl() in getInitData() in the constructor.
                return this.demoMode || wsParam === 'demo' || !wsParam
                    ? this.demoService.getEventsObservable()
                    : (super.getEventsObservable() as Observable<YEventPayload<AppEventType>>);
            })
        );
    }

    getStateObservable(): Observable<AppState | undefined> {
        return this.stateSubject.asObservable();
    }

    async sendDealerActionRequest(action: DealerConsoleAction): Promise<void> {
        const data: DealerActionRequestMessage = { action };
        await this.sendRequestMessage(DealerRequestType.DealerAction, data);
    }

    async sendSetFlagStateRequest(flag: FlagStateType, state: boolean): Promise<void> {
        const data: SetFlagStateRequestMessage = { flag, state };
        await this.sendRequestMessage(DealerRequestType.SetFlagState, data);
        this.updateLocalFlagState(flag, state);
    }

    async sendChangeGameRequest(gameType: GameType): Promise<void> {
        if (this.demoMode) {
            return;
        }

        const data: ChangeGameRequestMessage = { game: gameType };
        await this.sendRequestMessage(DealerRequestType.ChangeGame, data);
    }

    async sendGetChatMessagesRequest(): Promise<ChatMessageEvent[]> {
        if (this.demoMode) {
            return this.demoService.getChatMessages();
        }

        const response: GetChatMessagesResponseMessage = await this.sendRequestMessage<
            DealerRequestType.GetChatMessages,
            undefined,
            GetChatMessagesResponseMessage
        >(DealerRequestType.GetChatMessages);

        return response.messages;
    }

    async sendGetGamesRequest(): Promise<GameType[]> {
        if (this.demoMode) {
            return this.demoService.getGames();
        }
        const response: GetGamesResponseMessage = await this.sendRequestMessage<
            DealerRequestType.GetAvailableGames,
            undefined,
            GetGamesResponseMessage
        >(DealerRequestType.GetAvailableGames);

        return response.games;
    }

    /**
     * Loads `ws` query parameter and sets demoMode and returns web socket url.
     */
    private async determineDemoModeAndGetWsUrl(): Promise<string | undefined> {
        const wsParam = (await this.getQueryParams()).get('ws');
        if (wsParam === 'demo' || !wsParam) {
            this.demoMode = true;
        }

        if (this.demoMode) {
            return undefined;
        } else {
            // Return ws URL
            return wsParam === 'dev'
                ? 'wss://rocket.imaxagaming.com:9779/n/00d4fb50b11b0d9d/d52c740a7d741e64/croupier'
                : wsParam || environment.ws.url;
        }
    }

    private async getQueryParams(): Promise<ParamMap> {
        // Wrap call into setTimeout to avoid initial return of undefined!?!
        // https://github.com/angular/angular/issues/12157
        return new Promise((resolve) => setTimeout(() => resolve(this.route.snapshot.queryParamMap)));
    }

    private appendTimestampToUrl(url: string): string {
        return `${url}${url.endsWith('/') ? '' : '/'}${Date.now()}`;
    }

    private updateLocalFlagState(flag: FlagStateType, state: boolean): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(flag)] = state;
        this.stateSubject.next({ ...this.state });
    }

    private flagStateTypeToPropertyName(
        flag: FlagStateType
    ): 'chatEnabled' | 'roundBetsFree' | 'roundFlagged' | 'supervisorCalled' | 'tableClosingRequested' {
        switch (flag) {
            case FlagStateType.ChatEnabled:
                return 'chatEnabled';
            case FlagStateType.RoundFlagged:
                return 'roundFlagged';
            case FlagStateType.RoundBetsFree:
                return 'roundBetsFree';
            case FlagStateType.TableClosingRequested:
                return 'tableClosingRequested';
            case FlagStateType.SupervisorCalled:
                return 'supervisorCalled';
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown flag: ${flag}`);
        }
    }

    private handleLoginChange(event: LoginChangeEvent): void {
        if (event.new) {
            const { id, name } = event.new;
            this.state.croupier = { id, name };
        } else {
            this.state.croupier = undefined;
        }
        this.state.tableUpdate = undefined;
    }

    private handleGamePhaseChange(event: GamePhaseChangeEvent): void {
        this.state.roundState.phase = event.phase;

        if ([AppPhase.NewRoundPreparation, AppPhase.RoundVoid].includes(event.phase)) {
            this.state = {
                ...this.state,
                tableUpdate: undefined,
                shoeChangeState: undefined,
                flagsState: {
                    chatEnabled: this.state.flagsState.chatEnabled
                }
            };
        } else if (event.phase === AppPhase.DealingCards) {
            this.state.roundState.subPhase = event.subPhase;
        }

        if (event.phase === AppPhase.NewRoundPreparation) {
            this.state.roundState.roundId = (event as NewRoundPreparationPhaseChangeEvent).roundId;
        } else if (event.phase === AppPhase.RoundError) {
            this.state.roundState.errorCode = (event as RoundErrorPhaseChangeEvent).errorCode;
        } else if (event.phase === AppPhase.RoundEnd) {
            const roundEndEvent = event as RoundEndPhaseChangeEvent;
            this.state.tableUpdate = {
                outcome: roundEndEvent.outcome,
                outcomes: roundEndEvent.outcomes
            } as CardDealtEvent;
        }
    }

    private handleGameStateChangeEvent(event: GameStateChangeEvent): void {
        this.state = { ...event.state, games: this.state.games };
    }

    private handleCardBurn(event: CardBurnEvent): void {
        this.state.shoeChangeState = event;
    }

    private handleCardDealt(event: CardDealtEvent): void {
        this.state.tableUpdate = event;
    }

    private handleCutCardDrawn(): void {
        this.state.shoeChangeState = { ...this.state.shoeChangeState, cutCardDrawn: true };
    }

    private handleFlagStateChange(event: FlagStateChangeEvent): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(event.flag)] = event.state;
    }

    private handlePlayerCountChange(event: PlayerCountChangeEvent): void {
        this.state.playerCount = event.count;
    }

    private handleChatMessage(event: ChatMessageEvent): void {
        const messages = this.state.chatMessages || [];

        this.state.chatMessages =
            messages.length < DealerService.maxChatMessageDisplayCount ? [event, ...messages] : [event, ...messages.slice(0, -1)]; // Remove the oldest message (last element)
    }

    /**
     * In this method we only handle events that change the state of the game.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private handleYEvent(yEventPayload: YEventPayload<AppEventType>): void {
        const { data: event, name: appEventType } = yEventPayload;

        switch (appEventType) {
            case AppEventType.LoginChange:
                this.handleLoginChange(event as LoginChangeEvent);
                break;
            case AppEventType.GamePhaseChange:
                this.handleGamePhaseChange(event as GamePhaseChangeEvent);
                break;
            case AppEventType.GameStateChange:
                this.handleGameStateChangeEvent(event as GameStateChangeEvent);
                break;
            case AppEventType.CardBurn:
                this.handleCardBurn(event as CardBurnEvent);
                break;
            case AppEventType.CardDealt:
                this.handleCardDealt(event as CardDealtEvent);
                break;
            case AppEventType.CutCardDrawn:
                this.handleCutCardDrawn();
                break;
            case AppEventType.FlagStateChange:
                this.handleFlagStateChange(event as FlagStateChangeEvent);
                break;
            case AppEventType.PlayerCountChange:
                this.handlePlayerCountChange(event as PlayerCountChangeEvent);
                break;
            case AppEventType.ChatMessage:
                this.handleChatMessage(event as ChatMessageEvent);
                break;
            case AppEventType.Notification:
            default:
                return;
        }

        this.stateSubject.next({ ...this.state });
    }
}
